import React from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../components/Navbar/NavBar';
import { useTranslation } from 'react-i18next';
import { useProjectModal } from '../components/ProjectModal/ProjectModalContext';
import StepIcon from '../components/process/StepIcon';

const ArrowIcon = () => (
    <svg className="w-4 h-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
);

const HeroVisual = () => {
    const { t } = useTranslation();
    const steps = [1, 2, 3, 4, 5, 6].map((n) => ({
        n,
        label: t(`process.step${n}Label`),
        text: t(`process.step${n}Text`),
    }));

    return (
        <div className="rounded-2xl border border-slatex-200 bg-white shadow-lift p-6 lg:p-8">
            <div className="mb-6">
                <p className="eyebrow">{t('process.subtitle')}</p>
                <h3 className="mt-2 text-xl font-bold text-ink font-display">{t('process.title')}</h3>
            </div>

            <ol className="relative">
                {steps.map((s, i) => (
                    <li key={s.n} className={`relative flex gap-4 ${i === steps.length - 1 ? '' : 'pb-7'}`}>
                        {/* dotted connector */}
                        {i !== steps.length - 1 && (
                            <span className="absolute left-5 top-11 -bottom-1 w-px border-l-2 border-dashed border-slatex-300" aria-hidden="true" />
                        )}
                        {/* marker */}
                        <span className="relative z-10 flex h-10 w-10 flex-none items-center justify-center rounded-full bg-brand-50 text-brand-600 ring-4 ring-white">
                            <StepIcon index={s.n - 1} />
                        </span>
                        <div className="pt-1">
                            <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-brand-500 font-display">{String(s.n).padStart(2, '0')}</span>
                                <h4 className="text-sm font-bold text-ink">{s.label}</h4>
                            </div>
                            <p className="mt-1 text-sm text-slatex-600">{s.text}</p>
                        </div>
                    </li>
                ))}
            </ol>
        </div>
    );
};

const Hero = () => {
    const { t } = useTranslation();
    const { open } = useProjectModal();

    return (
        <div className="relative hero-surface" id="hero">
            <div className="pointer-events-none absolute inset-x-0 top-0 h-[520px] grid-mask" />
            <NavBar />

            <div className="container-x relative pt-28 pb-20 lg:pt-40 lg:pb-28">
                <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center">
                    {/* Copy */}
                    <div className="text-center lg:text-left" data-aos="fade-up">
                        <span className="eyebrow justify-center lg:justify-start">
                            <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
                            {t('ui.heroBadge')}
                        </span>
                        <h1 className="mt-5 text-4xl md:text-5xl xl:text-6xl font-extrabold leading-[1.1] text-ink">
                            {t('hero.title')}
                        </h1>
                        <p className="mt-6 text-lg text-slatex-600 max-w-xl mx-auto lg:mx-0">
                            {t('hero.description')}
                        </p>
                        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3">
                            <button type="button" onClick={open} className="btn-primary w-full sm:w-auto">
                                {t('navbar.cta')} <ArrowIcon />
                            </button>
                            <Link to="/about" className="btn-secondary w-full sm:w-auto">
                                {t('hero.learnMore')}
                            </Link>
                        </div>

                        {/* Stats */}
                        <div className="mt-12 grid grid-cols-3 gap-6 max-w-lg mx-auto lg:mx-0 border-t border-slatex-200 pt-8">
                            {[
                                { value: t('ui.statProjectsValue'), label: t('ui.statProjects') },
                                { value: `${new Date().getFullYear() - 2020 - 1}+`, label: t('ui.statYears') },
                                { value: t('ui.statSatisfactionValue'), label: t('ui.statSatisfaction') },
                            ].map((s) => (
                                <div key={s.label} className="text-center lg:text-left">
                                    <p className="text-2xl md:text-3xl font-extrabold text-ink font-display">{s.value}</p>
                                    <p className="mt-1 text-xs md:text-sm text-slatex-500">{s.label}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Visual */}
                    <div className="lg:pl-6" data-aos="fade-up" data-aos-delay="200">
                        <HeroVisual />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Hero;
